import CompletedIcon from '@assets/images/ttm/completed.png';
import ErrorIcon from '@assets/images/ttm/error.png';
import LinkExpiredIcon from '@assets/images/ttm/linkExpired.png';
import NoDataIcon from '@assets/images/ttm/noData.png';
import NotReadyIcon from '@assets/images/ttm/notReady.png';
import ProcessingIcon from '@assets/images/ttm/processing.png';
import StartTrackingIcon from '@assets/images/ttm/startTracking.png';
import InTransitIcon from '@assets/images/ttm/transit.png';

export enum STEPS {
	START_TRACKING,
	NO_DATA,
	LINK_EXPIRED,
	NOT_READY,
	ORDER_EXPIRED,
	PROCESSING,
	REJECTED,
	IN_TRANSIT,
	FAILED,
	COMPLETED,
	CANCELLED
}

export enum ACTIVITY_STATUS {
	SUCCESS = 'success',
	IN_PROGRESS = 'in_progress',
	UNSUCCESSFUL = 'unsuccessful'
}

export enum ACTIVITY_STATE {
	ORDER_CREATE = 'order_create',
	ORDER_DEPOSIT = 'order_deposit',
	ORDER_REVIEW = 'order_review',
	ORDER_TRANSFER = 'order_transfer',
	ORDER_EXPIRED = 'order_expired'
}

export enum ACTIVITY_ACTION {
	NOT_READY = 'not_ready',
	DEPOSITED = 'deposited',
	PROCESSING = 'processing',
	REJECTED = 'rejected',
	IN_TRANSIT = 'in_transit',
	COMPLETED = 'completed',
	FAILED = 'failed',
	CANCELLED = 'cancelled',
	EXPIRED = 'expired'
}

export enum PAYOUT_METHOD {
	BANK_ACCOUNT = 'Bank Transfer',
	DIGITAL_WALLET = 'Digital Wallet',
	CASH_PICKUP = 'Cash Pickup'
}

export const TIMESTAMP_HEADER = {
	[ACTIVITY_STATE.ORDER_REVIEW]: {
		[ACTIVITY_ACTION.PROCESSING]: 'PROCESSING_ORDER',
		[ACTIVITY_ACTION.COMPLETED]: 'ORDER_HAS_BEEN_REVIEWED_AND_PROCESSED',
		[ACTIVITY_ACTION.REJECTED]: 'ORDER_COULD_NOT_BE_PROCESSED'
	},
	[ACTIVITY_STATE.ORDER_TRANSFER]: {
		[ACTIVITY_ACTION.IN_TRANSIT]: 'FUNDS_ARE_IN_TRANSIT',
		[ACTIVITY_ACTION.FAILED]: 'TRANSFER_HAS_FAILED',
		[ACTIVITY_ACTION.COMPLETED]: 'FUNDS_SHOULD_HAVE_ARRIVED_AT_YOUR_BANK',
		[ACTIVITY_ACTION.CANCELLED]: 'TRANSFER_HAS_CANCELLED'
	}
};

export const TIMESTAMP_DESCRIPTION = {
	[ACTIVITY_STATE.ORDER_CREATE]: {
		[ACTIVITY_ACTION.COMPLETED]: 'ORDER_HAS_BEEN_CREATED'
	},
	[ACTIVITY_STATE.ORDER_DEPOSIT]: {
		[ACTIVITY_ACTION.DEPOSITED]: 'DEPOSIT_HAS_BEEN_MADE'
	},
	[ACTIVITY_STATE.ORDER_REVIEW]: {
		[ACTIVITY_ACTION.PROCESSING]: 'WE_ARE_REVIEWING_AND_PROCESSING_THE_ORDER',
		[ACTIVITY_ACTION.REJECTED]: 'PLEASE_REACH_OUT_TO_THE_SENDER_FOR_MORE_DETAILS'
	},
	[ACTIVITY_STATE.ORDER_TRANSFER]: {
		[ACTIVITY_ACTION.IN_TRANSIT]: 'WHEN_WILL_MY_MONEY_ARRIVE',
		[ACTIVITY_ACTION.FAILED]: 'PLEASE_REACH_OUT_TO_THE_SENDER_FOR_MORE_DETAILS',
		[ACTIVITY_ACTION.COMPLETED]: 'MONEY_NOT_IN_ACCOUNT_YET',
		[ACTIVITY_ACTION.CANCELLED]: 'PLEASE_REACH_OUT_TO_THE_SENDER_FOR_MORE_DETAILS'
	}
};

export const TIMESTAMP_HEADER_OVERRIDES = {
	[ACTIVITY_STATE.ORDER_TRANSFER]: {
		[ACTIVITY_ACTION.COMPLETED]: {
			[PAYOUT_METHOD.CASH_PICKUP]: 'TRANSFER_IS_COMPLETED'
		}
	}
};

export const TIMESTAMP_DESCRIPTION_OVERRIDES = {
	[ACTIVITY_STATE.ORDER_TRANSFER]: {
		[ACTIVITY_ACTION.COMPLETED]: {
			[PAYOUT_METHOD.CASH_PICKUP]: 'PLEASE_COLLECT_YOUR_FUNDS_AT_THE_DESIGNATED_LOCATION'
		}
	}
};

export const TIMESTAMP_TOOLTIP = {
	[ACTIVITY_STATE.ORDER_TRANSFER]: {
		[ACTIVITY_ACTION.IN_TRANSIT]: {
			[PAYOUT_METHOD.BANK_ACCOUNT]: true,
			[PAYOUT_METHOD.DIGITAL_WALLET]: true,
			[PAYOUT_METHOD.CASH_PICKUP]: true
		},
		[ACTIVITY_ACTION.COMPLETED]: {
			[PAYOUT_METHOD.BANK_ACCOUNT]: true,
			[PAYOUT_METHOD.DIGITAL_WALLET]: true,
			[PAYOUT_METHOD.CASH_PICKUP]: false
		}
	}
};

export const STEPS_MAPPING = {
	[ACTIVITY_STATE.ORDER_CREATE]: STEPS.NOT_READY,
	[ACTIVITY_STATE.ORDER_DEPOSIT]: STEPS.NOT_READY,
	[ACTIVITY_STATE.ORDER_EXPIRED]: STEPS.ORDER_EXPIRED,
	[ACTIVITY_STATE.ORDER_REVIEW]: {
		[ACTIVITY_ACTION.PROCESSING]: STEPS.PROCESSING,
		[ACTIVITY_ACTION.REJECTED]: STEPS.REJECTED
	},
	[ACTIVITY_STATE.ORDER_TRANSFER]: {
		[ACTIVITY_ACTION.IN_TRANSIT]: STEPS.IN_TRANSIT,
		[ACTIVITY_ACTION.FAILED]: STEPS.FAILED,
		[ACTIVITY_ACTION.COMPLETED]: STEPS.COMPLETED,
		[ACTIVITY_ACTION.CANCELLED]: STEPS.CANCELLED
	}
};

export const TRACKING_STATUS = {
	[STEPS.START_TRACKING]: {
		title: 'START_TRACKING',
		subtitle: 'ENTER_ORDER_NUMBER_TO_TRACK_TRANSFER_STATUS',
		icon: StartTrackingIcon
	},
	[STEPS.NO_DATA]: {
		title: 'UNABLE_TO_GET_TRACKING_DETAILS',
		subtitle: 'PLEASE_ENSURE_THE_ORDER_NUMBER_IS_CORRECT',
		icon: NoDataIcon
	},
	[STEPS.LINK_EXPIRED]: {
		title: 'LINK_EXPIRED',
		subtitle: 'THE_LINK_HAS_EXPIRED_AND_NO_LONGER_AVAILABLE_FOR_TRACKING',
		icon: LinkExpiredIcon
	},
	[STEPS.NOT_READY]: {
		title: 'ORDER_IS_NOT_READY_FOR_TRACKING',
		subtitle: 'PENDING_ACTION_BY_SENDER',
		icon: NotReadyIcon
	},
	[STEPS.ORDER_EXPIRED]: {
		title: 'ORDER_HAS_EXPIRED',
		subtitle: 'PLEASE_REACH_OUT_TO_THE_SENDER_FOR_DETAILS',
		icon: ErrorIcon
	},
	[STEPS.PROCESSING]: {
		title: 'PROCESSING',
		subtitle: 'ORDER_IS_BEING_REVIEWED',
		icon: ProcessingIcon
	},
	[STEPS.REJECTED]: {
		title: 'UNABLE_TO_PROCESS_ORDER',
		subtitle: 'PLEASE_REACH_OUT_TO_THE_SENDER_FOR_DETAILS',
		icon: ErrorIcon
	},
	[STEPS.IN_TRANSIT]: {
		title: 'FUNDS_ARE_ON_THEIR_WAY',
		subtitle: 'ESTIMATED_ARRIVAL',
		icon: InTransitIcon
	},
	[STEPS.FAILED]: {
		title: 'UNABLE_TO_PROCESS_ORDER',
		subtitle: 'PLEASE_REACH_OUT_TO_THE_SENDER_FOR_DETAILS',
		icon: ErrorIcon
	},
	[STEPS.COMPLETED]: {
		title: 'TRANSFER_COMPLETE',
		subtitle: 'YOU_SHOULD_HAVE_RECEIVED_THE_FUNDS_BY_NOW',
		icon: CompletedIcon
	},
	[STEPS.CANCELLED]: {
		title: 'ORDER_HAS_BEEN_CANCELLED',
		subtitle: 'PLEASE_REACH_OUT_TO_THE_SENDER_FOR_DETAILS',
		icon: ErrorIcon
	}
};
